/**
 * CtaButtonRenderer — cta-button
 * Call-to-action button with label and optional href.
 */
import React from "react";
import { EditableText } from "../EditableText";
import { AnimatedField } from "../../animations/AnimatedField";
import { resolveText, resolveTextOptional } from "../../utils/resolveText";

function fieldAnims(field, elementFallback) {
  if (field && typeof field === "object") {
    return [
      field.boxAnimation ?? elementFallback?.boxAnimation ?? null,
      field.boxExitAnimation ?? elementFallback?.boxExitAnimation ?? null,
    ];
  }
  return [elementFallback?.boxAnimation ?? null, elementFallback?.boxExitAnimation ?? null];
}

export function CtaButtonRenderer({
  element,
  editing,
  onChange,
  playing,
  isPaused,
  currentTimeSec,
}) {
  const label = resolveText(element.label || element.text || "Get Started");
  const href = element.href || "#";
  const subtext = element.subtext ? resolveTextOptional(element.subtext) : null;
  const [labelBox, labelExit] = fieldAnims(element.label ?? element.text, element);
  const [subtextBox, subtextExit] = fieldAnims(element.subtext, null);

  return (
    <div style={{ display: "inline-grid", gap: "var(--space-2)", justifyItems: "start" }}>
      <AnimatedField
        boxAnimation={labelBox}
        boxExitAnimation={labelExit}
        playing={playing}
        isPaused={isPaused}
        currentTimeSec={currentTimeSec}
      >
        <button
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: "var(--space-2)",
            padding: "0.85em 2em",
            borderRadius: "var(--radius-md)",
            background: "var(--color-bg-accent)",
            color: "var(--color-text-primary)",
            fontFamily: "var(--font-body)",
            fontSize: "var(--text-base)",
            fontWeight: 700,
            border: "none",
            cursor: "pointer",
            letterSpacing: "0.02em",
            whiteSpace: "nowrap",
          }}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
        >
          <EditableText
            value={label}
            editing={editing}
            onChange={(val) => onChange("label" in element ? "label" : "text", val)}
            placeholder="Button Text"
          />
          {/* Arrow icon */}
          <svg
            width="1.14em"
            height="1.14em"
            viewBox="0 0 16 16"
            fill="none"
            style={{ flexShrink: 0 }}
          >
            <path
              d="M3 8h10M9 4l4 4-4 4"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      </AnimatedField>
      {(subtext || editing) && (
        <AnimatedField
          boxAnimation={subtextBox}
          boxExitAnimation={subtextExit}
          playing={playing}
          isPaused={isPaused}
          currentTimeSec={currentTimeSec}
        >
          <span
            style={{
              fontFamily: "var(--font-body)",
              fontSize: "var(--text-xs)",
              color: "var(--color-text-muted)",
            }}
          >
            <EditableText
              value={subtext || ""}
              editing={editing}
              onChange={(val) => onChange("subtext", val)}
              placeholder="Subtext"
            />
          </span>
        </AnimatedField>
      )}
    </div>
  );
}

export default CtaButtonRenderer;
